// Small shared controls for the builder section editors. Kept deliberately plain
// (label + input) so the flat editors stay readable.
import {useState, type ReactNode} from 'react';
import type {LucideIcon} from 'lucide-react';
import {Popover, PopoverContent, PopoverTrigger} from '@common-web/components/ui/popover';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@common-web/components/ui/alert-dialog';
import {Plus, Trash2, Info, ChevronUp, ChevronDown, ChevronRight} from 'lucide-react';
import {Input} from '@common-web/components/ui/input';
import {Textarea} from '@common-web/components/ui/textarea';
import {Button} from '@common-web/components/ui/button';
import {Label} from '@common-web/components/ui/label';
import {cn} from '@common-web/lib/utils';
import type {E123Config, GroupConfig, HeadlineSegment} from '@common/enrollmentSite/types';

/**
 * A section editor edits a slice of the draft config. Most sections touch the
 * member-facing GroupConfig; the submission-mapping section also edits the
 * server-only E123Config via patchE123.
 */
export interface SectionProps {
  config: GroupConfig;
  patch: (updates: Partial<GroupConfig>) => void;
  e123: E123Config;
  patchE123: (updates: Partial<E123Config>) => void;
  // True once the site is published — the URL slug can no longer change.
  slugLocked?: boolean;
  // The site's identifier, and whether it's still unsaved. Asset uploads target an
  // existing site row, so they're disabled until the first save creates it.
  groupSlug: string;
  isNew: boolean;
}

export function Field({
  label,
  hint,
  children,
  hintTop = false,
  labelAccessory,
}: {
  label: string;
  hint?: string;
  children: ReactNode;
  /** Render the hint under the title instead of under the content — for tall groups
      where a bottom hint would be orphaned far from what it describes. */
  hintTop?: boolean;
  /** Small control shown next to the label, e.g. a rich-text info popover. */
  labelAccessory?: ReactNode;
}) {
  const hintEl = hint ? <p className='text-xs text-muted-foreground'>{hint}</p> : null;
  return (
    <div className='space-y-1.5'>
      <div className='flex items-center gap-1.5'>
        <Label className='text-xs font-semibold uppercase tracking-wide text-muted-foreground'>
          {label}
        </Label>
        {labelAccessory}
      </div>
      {hintTop && hintEl}
      {children}
      {!hintTop && hintEl}
    </div>
  );
}

export function TextField({
  label,
  hint,
  value,
  onChange,
  placeholder,
}: {
  label: string;
  hint?: string;
  value: string | undefined;
  onChange: (v: string) => void;
  placeholder?: string;
}) {
  return (
    <Field label={label} hint={hint}>
      <Input
        value={value ?? ''}
        placeholder={placeholder}
        onChange={(e) => onChange(e.target.value)}
      />
    </Field>
  );
}

export function TextArea({
  label,
  hint,
  value,
  onChange,
  rows = 3,
}: {
  label: string;
  hint?: string;
  value: string | undefined;
  onChange: (v: string) => void;
  rows?: number;
}) {
  return (
    <Field label={label} hint={hint}>
      <Textarea rows={rows} value={value ?? ''} onChange={(e) => onChange(e.target.value)} />
    </Field>
  );
}

// Explains the inline link tags (see renderRichText) that config copy supports.
function RichTextInfo() {
  const tags: [string, string][] = [
    ['<url>ctpa.com</url>', 'website link'],
    ['<tel>888-820-5687</tel>', 'click-to-call'],
    ['<email>cs@ctpa.com</email>', 'email link'],
    ['<app>Clever Health app</app>', 'opens the app-download popup'],
  ];
  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          type='button'
          aria-label='Formatting help'
          className='text-muted-foreground transition-colors hover:text-foreground'
        >
          <Info className='h-3.5 w-3.5' />
        </button>
      </PopoverTrigger>
      <PopoverContent align='start' className='w-80 space-y-2 text-xs'>
        <p className='font-medium text-foreground'>Add links with inline tags</p>
        <p className='text-muted-foreground'>
          Wrap the text you want linked — the address is inferred from the text, so you never retype
          it.
        </p>
        <ul className='space-y-1.5'>
          {tags.map(([tag, desc]) => (
            <li key={tag} className='flex flex-col'>
              <code className='rounded bg-muted px-1 py-0.5 font-mono text-[11px] text-foreground'>
                {tag}
              </code>
              <span className='mt-0.5 text-muted-foreground'>{desc}</span>
            </li>
          ))}
        </ul>
      </PopoverContent>
    </Popover>
  );
}

/** Textarea for copy that supports inline link tags — adds an info popover to the label. */
export function RichTextEligibleField({
  label,
  hint,
  value,
  onChange,
  rows = 2,
}: {
  label: string;
  hint?: string;
  value: string | undefined;
  onChange: (v: string) => void;
  rows?: number;
}) {
  return (
    <Field label={label} hint={hint} labelAccessory={<RichTextInfo />}>
      <Textarea rows={rows} value={value ?? ''} onChange={(e) => onChange(e.target.value)} />
    </Field>
  );
}

/** Editor for a HeadlineSegment[] — a headline split into runs, each optionally accent-colored. */
export function SegmentsEditor({
  label,
  hint,
  segments,
  onChange,
}: {
  label: string;
  hint?: string;
  segments: HeadlineSegment[];
  onChange: (next: HeadlineSegment[]) => void;
}) {
  function update(i: number, patch: Partial<HeadlineSegment>) {
    onChange(segments.map((seg, idx) => (idx === i ? {...seg, ...patch} : seg)));
  }
  return (
    <Field label={label} hint={hint}>
      <div className='space-y-2'>
        {segments.map((seg, i) => (
          <div key={i} className='flex items-center gap-2'>
            <Input
              value={seg.text}
              placeholder='Segment text'
              onChange={(e) => update(i, {text: e.target.value})}
            />
            <label className='flex shrink-0 items-center gap-1.5 text-xs text-muted-foreground'>
              <input
                type='checkbox'
                checked={!!seg.accent}
                onChange={(e) => update(i, {accent: e.target.checked})}
              />
              accent
            </label>
            <Button
              variant='ghost'
              size='icon'
              aria-label='Remove line'
              onClick={() => onChange(segments.filter((_, idx) => idx !== i))}
            >
              <Trash2 className='h-4 w-4' />
            </Button>
          </div>
        ))}
        <Button variant='outline' size='sm' onClick={() => onChange([...segments, {text: ''}])}>
          <Plus className='mr-1.5 h-3.5 w-3.5' />
          Add segment
        </Button>
      </div>
    </Field>
  );
}

/** Editor for a string[] (e.g. card bullets). */
export function StringListEditor({
  label,
  hint,
  items,
  onChange,
  addLabel = 'Add line',
}: {
  label: string;
  hint?: string;
  items: string[];
  onChange: (next: string[]) => void;
  addLabel?: string;
}) {
  return (
    <Field label={label} hint={hint}>
      <div className='space-y-2'>
        {items.map((item, i) => (
          <div key={i} className='flex items-center gap-2'>
            <Input
              value={item}
              onChange={(e) => onChange(items.map((v, idx) => (idx === i ? e.target.value : v)))}
            />
            <Button
              variant='ghost'
              size='icon'
              aria-label='Remove'
              onClick={() => onChange(items.filter((_, idx) => idx !== i))}
            >
              <Trash2 className='h-4 w-4' />
            </Button>
          </div>
        ))}
        <Button variant='outline' size='sm' onClick={() => onChange([...items, ''])}>
          <Plus className='mr-1.5 h-3.5 w-3.5' />
          {addLabel}
        </Button>
      </div>
    </Field>
  );
}

export type {GroupConfig};

/**
 * Checkbox for a config field that falls back to a shared default when omitted.
 * Checked = the field is unset and the site renders the default; unchecked = the site
 * carries its own copy. Re-checking asks first when the copy differs from the default.
 */
export function UseDefaultToggle({
  label,
  hint,
  usingDefault,
  isCustomized,
  onChange,
}: {
  label: string;
  hint?: string;
  usingDefault: boolean;
  /** The site's copy differs from the default — going back to default would lose edits. */
  isCustomized: boolean;
  onChange: (usingDefault: boolean) => void;
}) {
  const [confirmOpen, setConfirmOpen] = useState(false);
  return (
    <Field label={label} hint={hint}>
      <label className='flex items-center gap-2 text-sm'>
        <input
          type='checkbox'
          checked={usingDefault}
          onChange={(e) => {
            if (e.target.checked && isCustomized) setConfirmOpen(true);
            else onChange(e.target.checked);
          }}
        />
        Use the shared default
      </label>
      <AlertDialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Go back to the shared default?</AlertDialogTitle>
            <AlertDialogDescription>
              This site's own {label.toLowerCase()} will be discarded and it will render the shared
              default again.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Keep editing</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                setConfirmOpen(false);
                onChange(true);
              }}
            >
              Use default
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Field>
  );
}

/** Deep-equal on config blobs — the same JSON round-trip a saved draft goes through. */
export function sameConfig(a: unknown, b: unknown): boolean {
  return JSON.stringify(a ?? null) === JSON.stringify(b ?? null);
}

/** Numbered, reorderable, removable frame around one item of a list editor. */
export function EditorCard({
  index,
  count,
  title,
  subtitle,
  onMove,
  onRemove,
  collapsible = false,
  defaultOpen = false,
  children,
}: {
  index: number;
  count: number;
  title: string;
  /** Muted text after the title, e.g. where a card links. */
  subtitle?: string;
  onMove: (dir: -1 | 1) => void;
  onRemove: () => void;
  /** Header click toggles the body; starts closed unless `defaultOpen`. */
  collapsible?: boolean;
  defaultOpen?: boolean;
  children: ReactNode;
}) {
  const [open, setOpen] = useState(!collapsible || defaultOpen);
  const toggle = () => setOpen((v) => !v);
  return (
    <div className='overflow-hidden rounded-lg border'>
      <div className={cn('flex items-center gap-2 bg-muted/30 px-3 py-2', open && 'border-b')}>
        {/* A div, not a button: a mirror lock's disabled fieldset must not stop expanding. */}
        <div
          role={collapsible ? 'button' : undefined}
          tabIndex={collapsible ? 0 : undefined}
          aria-expanded={collapsible ? open : undefined}
          onClick={collapsible ? toggle : undefined}
          onKeyDown={
            collapsible
              ? (e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    toggle();
                  }
                }
              : undefined
          }
          className={cn('flex min-w-0 flex-1 items-center gap-2', collapsible && 'cursor-pointer')}
        >
          {collapsible && (
            <ChevronRight
              className={cn('h-4 w-4 shrink-0 text-muted-foreground transition-transform', open && 'rotate-90')}
            />
          )}
          <span className='flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-primary/10 text-xs font-semibold text-primary'>
            {index + 1}
          </span>
          <span className='truncate text-sm font-semibold'>{title}</span>
          {subtitle && <span className='truncate text-xs text-muted-foreground'>{subtitle}</span>}
        </div>
        <Button
          variant='ghost'
          size='icon'
          className='h-7 w-7'
          aria-label='Move up'
          disabled={index === 0}
          onClick={() => onMove(-1)}
        >
          <ChevronUp className='h-4 w-4' />
        </Button>
        <Button
          variant='ghost'
          size='icon'
          className='h-7 w-7'
          aria-label='Move down'
          disabled={index === count - 1}
          onClick={() => onMove(1)}
        >
          <ChevronDown className='h-4 w-4' />
        </Button>
        <Button
          variant='ghost'
          size='icon'
          className='h-7 w-7 text-destructive hover:text-destructive'
          aria-label='Remove'
          onClick={onRemove}
        >
          <Trash2 className='h-4 w-4' />
        </Button>
      </div>
      {open && <div className='space-y-3 p-3'>{children}</div>}
    </div>
  );
}

/** Swap items i and i+dir; returns the same array when the move is out of range. */
export function moveItem<T>(items: T[], i: number, dir: -1 | 1): T[] {
  const j = i + dir;
  if (j < 0 || j >= items.length) return items;
  const next = [...items];
  [next[i], next[j]] = [next[j], next[i]];
  return next;
}

/** Option for IconSelect — a real icon component + label so the picker shows what it selects. */
export interface IconOption {
  value: string;
  label: string;
  Icon: LucideIcon;
  color?: string;
}

/**
 * Compact icon picker: a single-glyph trigger that opens a grid of the icon pool.
 * Icon-only (no names) and inline-friendly. Reusable for any IconOption[] pool.
 */
export function IconSelect({
  value,
  onChange,
  options,
  columns = 5,
}: {
  value: string;
  onChange: (v: string) => void;
  options: IconOption[];
  columns?: 4 | 5;
}) {
  const [open, setOpen] = useState(false);
  const Selected = options.find((o) => o.value === value)?.Icon;
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button variant='outline' size='icon' className='h-9 w-9 bg-white' title='Choose an icon'>
          {Selected ? (
            <Selected className='h-4 w-4' />
          ) : (
            <Plus className='h-4 w-4 text-muted-foreground' />
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent className='w-auto p-2'>
        <div className={cn('grid gap-1', columns === 4 ? 'grid-cols-4' : 'grid-cols-5')}>
          {options.map(({value: v, label, Icon}) => (
            <button
              key={v}
              type='button'
              title={label}
              onClick={() => {
                onChange(v);
                setOpen(false);
              }}
              className={cn(
                'flex h-9 w-9 items-center justify-center rounded-md border',
                v === value
                  ? 'border-primary bg-primary/5 text-primary'
                  : 'border-transparent text-foreground hover:bg-muted',
              )}
            >
              <Icon className='h-4 w-4' />
            </button>
          ))}
        </div>
      </PopoverContent>
    </Popover>
  );
}

/** A row of selectable color swatches. The pool is passed in, so it's reusable with any palette. */
export function ColorSwatchField({
  label,
  hint,
  value,
  onChange,
  colors,
  hintTop = false,
}: {
  label: string;
  hint?: string;
  value: string;
  onChange: (v: string) => void;
  colors: {value: string; label: string}[];
  hintTop?: boolean;
}) {
  return (
    <Field label={label} hint={hint} hintTop={hintTop}>
      <div className='flex flex-wrap gap-2'>
        {colors.map(({value: c, label: l}) => (
          <button
            key={c}
            type='button'
            title={l}
            onClick={() => onChange(c)}
            style={{backgroundColor: c}}
            className={cn(
              'h-7 w-7 rounded-full transition',
              value === c
                ? 'ring-2 ring-foreground/60 ring-offset-2'
                : 'ring-1 ring-black/10 hover:ring-foreground/30',
            )}
          />
        ))}
      </div>
    </Field>
  );
}

/**
 * Enclosed icon + color picker: a live preview chip (the icon in its color) that
 * opens one popover with both an icon grid and a color swatch row. Pools are passed
 * in, so it's reusable with any icon/color set.
 */
export function IconColorPicker({
  icon,
  color,
  onIconChange,
  onColorChange,
  icons,
  colors,
}: {
  icon: string;
  color: string;
  onIconChange: (v: string) => void;
  onColorChange: (v: string) => void;
  icons: IconOption[];
  colors: {value: string; label: string}[];
}) {
  const Selected = icons.find((o) => o.value === icon)?.Icon;
  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          type='button'
          title='Choose icon & color'
          className='flex h-10 w-10 items-center justify-center rounded-lg border bg-white transition hover:bg-muted/40'
          style={{borderColor: color}}
        >
          {Selected ? (
            <Selected className='h-5 w-5' style={{color}} />
          ) : (
            <Plus className='h-4 w-4 text-muted-foreground' />
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent align='start' className='w-60 space-y-3 p-3'>
        <div className='space-y-1.5'>
          <div className='text-[11px] font-semibold uppercase tracking-wide text-muted-foreground'>
            Icon
          </div>
          <div className='grid grid-cols-5 gap-1'>
            {icons.map(({value: v, label, Icon}) => (
              <button
                key={v}
                type='button'
                title={label}
                onClick={() => onIconChange(v)}
                className={cn(
                  'flex h-9 w-9 items-center justify-center rounded-md border',
                  v === icon ? 'border-primary' : 'border-transparent hover:bg-muted',
                )}
              >
                <Icon className='h-4 w-4' />
              </button>
            ))}
          </div>
        </div>
        <div className='space-y-1.5'>
          <div className='text-[11px] font-semibold uppercase tracking-wide text-muted-foreground'>
            Color
          </div>
          <div className='flex flex-wrap gap-1.5'>
            {colors.map(({value: c, label: l}) => (
              <button
                key={c}
                type='button'
                title={l}
                onClick={() => onColorChange(c)}
                style={{backgroundColor: c}}
                className={cn(
                  'h-6 w-6 rounded-full transition',
                  c === color
                    ? 'ring-2 ring-foreground/60 ring-offset-2'
                    : 'ring-1 ring-black/10 hover:ring-foreground/30',
                )}
              />
            ))}
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
}
